import { baseApi } from '../../../platform/api/base-api'
import { browserTokenStorage } from '../../../platform/auth/browser-token-storage'
import { confirmVerifiedProfile } from '../application/verified-profile'
import {
  authMethodsResponseSchema,
  currentUserSchema,
  messageResponseSchema,
  oauthStartResponseSchema,
  profileLimitsSchema,
  publicUserSchema,
  profileStatsDtoSchema,
  tokenResponseSchema,
  type AuthMethods,
  type CurrentUser,
  type MessageResponse,
  type OAuthStartResponse,
  type ProfileLimits,
  type PublicUser,
  type ReviewMode,
  type ThemeMode,
  type TokenResponse,
} from '../domain/contracts'
import type {
  ForgotPasswordInput,
  LoginInput,
  SignedEmailLink,
} from '../domain/auth'
import type { ProfileUpdate } from '../domain/profile'
import type { ProfileStats } from '../domain/profile-stats'
import { parseApiData } from './api-contract'
import { toProfileStats } from './profile-stats-mapper'
import { getRegistrationSource } from '../../../platform/analytics/registration-source'
import { getReferralCode } from '../../../platform/referral/referral-attribution'

const DEVICE_NAME = 'card-replay-web-v2'

type RegistrationRequest = {
  name: string
  email: string
  password: string
}

type ResetPasswordRequest = SignedEmailLink & {
  password: string
}

export const identityApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAuthMethods: builder.query<AuthMethods, void>({
      query: () => '/auth/methods',
      transformResponse: (response: unknown) =>
        parseApiData(authMethodsResponseSchema, response),
    }),
    login: builder.mutation<TokenResponse, LoginInput>({
      query: ({ email, password }: LoginInput) => {
        return {
          url: '/auth/token',
          method: 'POST',
          body: {
            email,
            password,
            device_name: DEVICE_NAME,
          },
        }
      },
      transformResponse: (response: unknown) =>
        parseApiData(tokenResponseSchema, response),
      invalidatesTags: ['Profile'],
    }),
    register: builder.mutation<MessageResponse, RegistrationRequest>({
      query: ({ name, email, password }: RegistrationRequest) => {
        const registrationSource = getRegistrationSource()
        const referralCode = getReferralCode()
        return {
          url: '/auth/registration',
          method: 'POST',
          body: {
            name,
            email,
            password,
            surname: null,
            device_name: DEVICE_NAME,
            ...(registrationSource
              ? { registration_source: registrationSource }
              : {}),
            ...(referralCode ? { referral_code: referralCode } : {}),
          },
        }
      },
      transformResponse: (response: unknown) =>
        parseApiData(messageResponseSchema, response),
    }),
    startOAuth: builder.mutation<OAuthStartResponse, { provider: string }>({
      query: ({ provider }) => {
        const referralCode = getReferralCode()
        return {
          url: `/auth/oauth/${encodeURIComponent(provider)}/start`,
          method: 'POST',
          body: {
            mobile: false,
            registration_source: getRegistrationSource(),
            ...(referralCode ? { referral_code: referralCode } : {}),
          },
        }
      },
      transformResponse: (response: unknown) =>
        parseApiData(oauthStartResponseSchema, response),
    }),
    confirmEmail: builder.mutation<TokenResponse, SignedEmailLink>({
      query: ({ id, expires, sign }: SignedEmailLink) => ({
        url: '/auth/confirm',
        method: 'POST',
        body: { id, expires, sign, device_name: DEVICE_NAME },
      }),
      transformResponse: (response: unknown) =>
        parseApiData(tokenResponseSchema, response),
      invalidatesTags: ['Profile'],
    }),
    forgotPassword: builder.mutation<MessageResponse, ForgotPasswordInput>({
      query: (body: ForgotPasswordInput) => ({
        url: '/auth/forgot',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        parseApiData(messageResponseSchema, response),
    }),
    resetPassword: builder.mutation<MessageResponse, ResetPasswordRequest>({
      query: ({ id, expires, sign, password }: ResetPasswordRequest) => ({
        url: '/auth/reset',
        method: 'POST',
        body: { id, expires, sign, password },
      }),
      transformResponse: (response: unknown) =>
        parseApiData(messageResponseSchema, response),
    }),
    revokeSession: builder.mutation<MessageResponse, void>({
      query: () => ({ url: '/auth/token/revoke', method: 'POST' }),
      transformResponse: (response: unknown) =>
        parseApiData(messageResponseSchema, response),
    }),
    getCurrentUser: builder.query<CurrentUser, void>({
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        try {
          const { data, meta } = await queryFulfilled
          const authorization = meta?.request.headers.get('authorization')
          const token = browserTokenStorage.read()
          if (token && authorization === `Bearer ${token}`)
            confirmVerifiedProfile(data.id, token)
        } catch {
          /* A failed or stale profile cannot verify a session. */
        }
      },
      query: () => '/profile',
      transformResponse: (response: unknown) =>
        parseApiData(currentUserSchema, response),
      providesTags: ['Profile'],
    }),
    getPublicUser: builder.query<PublicUser, number>({
      query: (userId) => `/users/${String(userId)}`,
      transformResponse: (response: unknown) =>
        parseApiData(publicUserSchema, response),
    }),
    getProfileStats: builder.query<ProfileStats, void>({
      query: () => '/profile/stats',
      transformResponse: (response: unknown) => {
        const dto = parseApiData(profileStatsDtoSchema, response)
        return toProfileStats(dto)
      },
      providesTags: ['Profile'],
    }),
    getProfileLimits: builder.query<ProfileLimits, void>({
      query: () => '/profile/limits',
      transformResponse: (response: unknown) =>
        parseApiData(profileLimitsSchema, response),
      providesTags: [{ id: 'LIMITS', type: 'Profile' }],
    }),
    updateTheme: builder.mutation<CurrentUser, ThemeMode>({
      query: (theme: ThemeMode) => ({
        url: '/profile/settings',
        method: 'POST',
        body: { theme },
      }),
      transformResponse: (response: unknown) =>
        parseApiData(currentUserSchema, response),
      invalidatesTags: ['Profile'],
    }),
    updateReviewMode: builder.mutation<CurrentUser, ReviewMode>({
      query: (defaultReviewMode: ReviewMode) => ({
        url: '/profile/settings',
        method: 'POST',
        body: { default_review_mode: defaultReviewMode },
      }),
      transformResponse: (response: unknown) =>
        parseApiData(currentUserSchema, response),
      invalidatesTags: ['Profile'],
    }),
    updatePprSessionCardLimit: builder.mutation<CurrentUser, number>({
      query: (limit) => ({
        url: '/profile/settings',
        method: 'POST',
        body: { ppr_session_card_limit: limit },
      }),
      transformResponse: (response: unknown) =>
        parseApiData(currentUserSchema, response),
      onQueryStarted: async (_limit, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(
            identityApi.util.updateQueryData(
              'getCurrentUser',
              undefined,
              (current) => {
                if (current.id === data.id) current.settings = data.settings
              },
            ),
          )
        } catch {
          /* Keep the saved preference on failure. */
        }
      },
      invalidatesTags: ['Profile'],
    }),
    updateProfile: builder.mutation<CurrentUser, ProfileUpdate>({
      query: (profile) => ({
        body: {
          ...(profile.gender !== undefined
            ? {
                gender: profile.gender,
                ...(profile.gender === null ? { clear_gender: true } : {}),
              }
            : {}),
          birthday_date: profile.birthdayDate,
          ...(profile.birthdayDate === null
            ? { clear_birthday_date: true }
            : {}),
          description: profile.description,
          image_path: profile.imagePath,
          lang: profile.lang,
          name: profile.name,
          surname: profile.surname,
        },
        method: 'PUT',
        url: '/profile',
      }),
      transformResponse: (response: unknown, _meta, request) => {
        const parsed = parseApiData(currentUserSchema, response)
        if (
          request.gender !== undefined &&
          (parsed.gender !== request.gender ||
            !(
              response &&
              typeof response === 'object' &&
              'data' in response &&
              response.data &&
              typeof response.data === 'object' &&
              'gender' in response.data
            ))
        ) {
          throw new Error(
            'Сервер не подтвердил сохранение пола. Обновите страницу и попробуйте снова.',
          )
        }
        return parsed
      },
      invalidatesTags: ['Profile'],
    }),
    updateProfileDescription: builder.mutation<CurrentUser, string | null>({
      query: (description) => ({
        body: { description },
        method: 'PUT',
        url: '/profile/desc',
      }),
      transformResponse: (response: unknown) =>
        parseApiData(currentUserSchema, response),
      invalidatesTags: ['Profile'],
    }),
    deleteProfile: builder.mutation<void, void>({
      query: () => ({ method: 'DELETE', url: '/profile' }),
    }),
  }),
})

export const {
  useConfirmEmailMutation,
  useForgotPasswordMutation,
  useGetAuthMethodsQuery,
  useGetCurrentUserQuery,
  useGetProfileLimitsQuery,
  useGetPublicUserQuery,
  useGetProfileStatsQuery,
  useLoginMutation,
  useRegisterMutation,
  useResetPasswordMutation,
  useStartOAuthMutation,
  useRevokeSessionMutation,
  useDeleteProfileMutation,
  useUpdateProfileDescriptionMutation,
  useUpdateProfileMutation,
  useUpdateReviewModeMutation,
  useUpdatePprSessionCardLimitMutation,
  useUpdateThemeMutation,
} = identityApi
